export enum OperationStatusEnum {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export interface OperationEntry {
  operationUuid: string;
  label: string;
  status: OperationStatusEnum;
  startedAt: number;
}

export interface AppOperationsState {
  operations: OperationEntry[];
}

export enum AppOperationsActionEnum {
  OPERATION_STARTED = "OPERATION_STARTED",
  OPERATION_RESOLVED = "OPERATION_RESOLVED",
  OPERATION_REMOVED = "OPERATION_REMOVED",
  OPERATIONS_CLEAR_RESOLVED = "OPERATIONS_CLEAR_RESOLVED",
}

type Action =
  | {
      type: AppOperationsActionEnum.OPERATION_STARTED;
      value: { operationUuid: string; label: string; startedAt: number };
    }
  | {
      type: AppOperationsActionEnum.OPERATION_RESOLVED;
      value: {
        operationUuid: string;
        status: OperationStatusEnum.COMPLETED | OperationStatusEnum.FAILED;
      };
    }
  | { type: AppOperationsActionEnum.OPERATION_REMOVED; value: string }
  | { type: AppOperationsActionEnum.OPERATIONS_CLEAR_RESOLVED };

const initialState: AppOperationsState = { operations: [] };

const appOperationsReducer = (
  state: AppOperationsState = initialState,
  action: Action,
): AppOperationsState => {
  switch (action.type) {
    case AppOperationsActionEnum.OPERATION_STARTED: {
      if (state.operations.some((o) => o.operationUuid === action.value.operationUuid))
        return state;
      return {
        operations: [
          {
            operationUuid: action.value.operationUuid,
            label: action.value.label,
            status: OperationStatusEnum.PENDING,
            startedAt: action.value.startedAt,
          },
          ...state.operations,
        ],
      };
    }
    case AppOperationsActionEnum.OPERATION_RESOLVED:
      return {
        operations: state.operations.map((o) =>
          o.operationUuid === action.value.operationUuid && o.status === OperationStatusEnum.PENDING
            ? { ...o, status: action.value.status }
            : o,
        ),
      };
    case AppOperationsActionEnum.OPERATION_REMOVED:
      return { operations: state.operations.filter((o) => o.operationUuid !== action.value) };
    case AppOperationsActionEnum.OPERATIONS_CLEAR_RESOLVED:
      return {
        operations: state.operations.filter((o) => o.status === OperationStatusEnum.PENDING),
      };
    default:
      return state;
  }
};

export default appOperationsReducer;

export const operationStartedAction = (
  operationUuid: string,
  label: string,
  startedAt: number,
) => ({
  type: AppOperationsActionEnum.OPERATION_STARTED as const,
  value: { operationUuid, label, startedAt },
});

export const operationResolvedAction = (
  operationUuid: string,
  status: OperationStatusEnum.COMPLETED | OperationStatusEnum.FAILED,
) => ({
  type: AppOperationsActionEnum.OPERATION_RESOLVED as const,
  value: { operationUuid, status },
});

export const operationRemovedAction = (operationUuid: string) => ({
  type: AppOperationsActionEnum.OPERATION_REMOVED as const,
  value: operationUuid,
});

export const operationsClearResolvedAction = () => ({
  type: AppOperationsActionEnum.OPERATIONS_CLEAR_RESOLVED as const,
});
